import { Box, Button, Stack, Typography, styled } from '@mui/material';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { BillingStatus } from 'src/@types/billing';
import { routes } from 'src/Router/paths';
import RoleBasedGuard from 'src/auth/RoleBasedGuard';
import { useAuthContext } from 'src/auth/useAuthContext.js';
import { useConfirmationModal } from 'src/components/ConfirmationModal/index.js';
import { Iconify } from 'src/components/iconify';
import { useSubscription } from 'src/hooks/useSubscription';
import { useLocales } from 'src/locales';
import { useSelector } from 'src/redux/store';
import { Rule } from 'src/requests/roles.requests';
import { WorkspaceRequests } from 'src/requests/workspace.requests.js';
import {
  appLocationState,
  getCurrentSession,
  getSessionByUserId,
  getSessionSlug,
} from 'src/state';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { GlobalAlert } from '../DashboardLayout/GlobalAlert/GlobalAlert';
import { useGlobalAlert } from '../DashboardLayout/GlobalAlert/useGlobalAlert';
import { MobileBar } from '../DashboardLayout/MobileBar';
import { TrialEndingAlert } from '../DashboardLayout/TrialEndingAlert';
import { VerifyAccountAlert } from '../DashboardLayout/VerifyAccountAlert/VerifyAccountAlert.js';
import { AppGrid } from '../shared/AppGrid';
import Header from '../shared/Header';
import { LayoutMainContainer } from '../shared/LayoutMainContainer';
import { NavLink } from '../shared/NavLInk';
import { ResponsiveVerticalNav } from '../shared/ResponsiveVerticalNav';

const Content = styled(Box)(() => ({
  display: 'flex',
  flexGrow: 1,
  width: '100%',
  gridArea: 'content',
  position: 'relative',
  overflow: 'hidden',
}));

export const SettingsLayout = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);

  const { workspaceLocked, subscription } = useSubscription();

  const { workspace } = useSelector((state) => state.workspace);

  const { alert } = useGlobalAlert();

  const { user, logout } = useAuthContext();

  const { translate } = useLocales();

  const isOwner = workspace.owner === user?._id;

  const { isDesktop, isMobile } = useBreakpoints();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const navigate = useNavigate();

  const { confirm } = useConfirmationModal();

  const handleLeaveOrDeleteWorkspace = async (
    leaveOrDelete: () => Promise<{ _id: string }>,
  ) => {
    try {
      const { _id } = await leaveOrDelete();
      const { removeSession } = appLocationState.getState();
      removeSession(_id);

      const otherWorkspaces = user?.myUsers.filter(
        (item) => item.workspace._id !== workspace._id,
      );

      if (otherWorkspaces && otherWorkspaces.length > 0) {
        const nextWorkspace = otherWorkspaces[0];
        const session = getSessionByUserId(nextWorkspace._id);
        if (session) {
          window.location.replace(
            `/${getSessionSlug(session)}/${session.lastPath}`,
          );
          return;
        }
      }
      logout();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.response?.data ||
          'Error on leave workspace',
        {
          variant: 'error',
        },
      );
    }
  };

  const handleLeaveWorkspace = async () => {
    if (
      !(await confirm({
        title: translate('shared.confirmation.are_you_sure_title'),
        message: (
          <Typography>
            {translate('shared.confirmation.are_you_sure_leave_workspace')}
          </Typography>
        ),
      }))
    )
      return;

    handleLeaveOrDeleteWorkspace(() => WorkspaceRequests.leaveWorkspace());
  };

  const handleDeleteWorkspace = async () => {
    if (
      !(await confirm({
        title: translate('shared.confirmation.are_you_sure_title'),
        message: (
          <Typography>
            {translate('shared.confirmation.are_you_sure_delete_workspace')}
          </Typography>
        ),
      }))
    )
      return;

    handleLeaveOrDeleteWorkspace(() => WorkspaceRequests.deleteWorkspace());
  };
  return (
    <AppGrid
      className={cn({
        hasNotification: isDesktop && (!!alert || !user?.verified.email),
      })}
    >
      {isDesktop && !user?.verified.email && <VerifyAccountAlert />}

      {!!alert && isDesktop && (
        <Box sx={{ gridArea: 'notification' }}>
          <GlobalAlert />
        </Box>
      )}

      <Box sx={{ gridArea: 'header' }}>
        <Header />
      </Box>

      <Content>
        <ResponsiveVerticalNav openNav={open} onCloseNav={handleClose}>
          <Stack flexDirection="column" px={2}>
            <Button
              sx={{ mt: 3 }}
              variant="soft"
              color="primary"
              onClick={() => {
                const session = getCurrentSession();
                navigate(`/${getSessionSlug(session)}/${session.lastPath}`);
              }}
              startIcon={<Iconify icon="mdi:arrow-left" />}
            >
              {translate('shared.buttons.back_to_workspace')}
            </Button>
            <Typography variant="overline" mt={3} mb={1.5}>
              {translate('settings.nav_lateral.items.account')}
            </Typography>
            <NavLink
              title={translate('settings.nav_lateral.items.my_account')}
              icon={<Iconify icon="mdi:person" />}
              path={routes.settings.user.account}
            />

            <NavLink
              title={translate('settings.nav_lateral.items.security')}
              icon={<Iconify icon="mdi:shield-account-outline" />}
              path={routes.settings.user.security}
            />

            {/*<NavLink*/}
            {/*  title={translate('settings.nav_lateral.items.notifications')}*/}
            {/*  icon={<Iconify icon="mdi:bell-outline" />}*/}
            {/*  path={routes.settings.user.notifications}*/}
            {/*/>*/}

            <NavLink
              title={translate('settings.nav_lateral.items.language_&_region')}
              icon={<Iconify icon="mdi:earth" />}
              path={routes.settings.user.language}
            />

            <Typography variant="overline" mt={3} mb={1.5}>
              {translate('settings.nav_lateral.items.workspace')}
            </Typography>

            <RoleBasedGuard rule={Rule.ADMINISTRATOR}>
              <NavLink
                title={translate('settings.nav_lateral.items.my_workspace')}
                icon={<Iconify icon="mdi:office-building" />}
                path={
                  workspaceLocked ? '#!' : routes.settings.workspace.general
                }
                disabled={workspaceLocked}
                aria-disabled={workspaceLocked}
              />
            </RoleBasedGuard>

            <NavLink
              title={translate('shared.labels.members')}
              icon={<Iconify icon="mdi:account-group-outline" />}
              path={workspaceLocked ? '#!' : routes.settings.workspace.members}
              disabled={workspaceLocked}
              aria-disabled={workspaceLocked}
            />

            <RoleBasedGuard rule={Rule.ADMINISTRATOR}>
              <NavLink
                title={translate('header.workspace_dropdown.billing_item')}
                icon={<Iconify icon="mdi:credit-card-outline" />}
                path={routes.settings.workspace.billing}
              />

              <NavLink
                title={translate('settings.nav_lateral.items.teams')}
                icon={<Iconify icon="mdi:account-group" />}
                path={workspaceLocked ? '#!' : routes.settings.workspace.teams}
                disabled={workspaceLocked}
                aria-disabled={workspaceLocked}
              />

              {/* <NavLink
                title={translate('settings.nav_lateral.items.roles')}
                icon={<Iconify icon="mdi:account-key-outline" />}
                path={workspaceLocked ? '#!' : routes.settings.workspace.roles}
                disabled={workspaceLocked}
                aria-disabled={workspaceLocked}
              /> */}
            </RoleBasedGuard>

            <Button
              sx={{ mt: 3 }}
              variant="soft"
              color="error"
              onClick={handleDeleteWorkspace}
              startIcon={
                <Iconify icon="material-symbols:folder-delete-outline" />
              }
            >
              {translate('shared.buttons.delete_workspace')}
            </Button>

            {!isOwner && (
              <Button
                sx={{ mt: 3 }}
                variant="soft"
                color="error"
                onClick={handleLeaveWorkspace}
                startIcon={<Iconify icon="pepicons-pencil:leave" />}
              >
                {translate('shared.buttons.leave_workspace')}
              </Button>
            )}
          </Stack>
        </ResponsiveVerticalNav>

        <LayoutMainContainer>
          {subscription?.status === BillingStatus.TRIAL_WILL_END &&
          !subscription.cards.length ? (
            <TrialEndingAlert endAt={subscription?.trial_end} />
          ) : null}
          <Outlet />
        </LayoutMainContainer>
      </Content>

      {isMobile && (
        <Box sx={{ gridArea: 'mobileBar' }}>
          <MobileBar onOpenNav={handleOpen} />
        </Box>
      )}
    </AppGrid>
  );
};
